import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle2, Loader2, Phone, Clock, Target, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Intake() {
  const { language, t } = useLanguage();
  const basePath = language === 'en' ? '/en' : '';
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        lead_type: 'intake',
        language
      });

      setSubmitted(true);
      toast.success(t('quickScan.form.success'));
    } catch (error) {
      console.error('Intake form error:', error);
      toast.error(t('quickScan.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <Clock className="h-6 w-6" />,
      title: language === 'nl' ? '30 Minuten' : '30 Minutes',
      description: language === 'nl' 
        ? 'Een korte maar gerichte discovery call om je situatie te begrijpen.'
        : 'A short but focused discovery call to understand your situation.'
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: language === 'nl' ? 'Doelgericht' : 'Targeted',
      description: language === 'nl'
        ? 'We identificeren high-priority producten en concurrenten.'
        : 'We identify high-priority products and competitors.'
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: language === 'nl' ? 'Resource Check' : 'Resource Check',
      description: language === 'nl'
        ? 'Evaluatie van je beschikbare resources voor een realistische strategie.'
        : 'Evaluation of your available resources for a realistic strategy.'
    }
  ];

  if (submitted) {
    return (
      <div data-testid="intake-success" className="min-h-[80vh] flex items-center justify-center">
        <div className="container-kesult text-center max-w-2xl">
          <div className="w-20 h-20 bg-[#4286F4] rounded-2xl flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="h-10 w-10 text-white" />
          </div>
          <h1 className="heading-2 mb-4">
            {language === 'nl' ? 'BEDANKT!' : 'THANK YOU!'}
          </h1>
          <p className="body-large text-[#71717A] mb-8">
            {t('quickScan.form.success')}
          </p>
          <Link to={`${basePath}/`}>
            <Button className="btn-primary">
              {language === 'nl' ? 'Terug naar home' : 'Back to home'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="intake-page">
      {/* Hero */}
      <section className="section-spacing bg-[#4286F4] text-white">
        <div className="container-kesult">
          <div className="max-w-3xl">
            <Phone className="h-12 w-12 mb-6 opacity-80" />
            <h1 className="heading-1 mb-6" data-testid="intake-title">
              {t('quickScan.title')}
            </h1>
            <p className="body-large opacity-90">
              {t('quickScan.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-spacing bg-white">
        <div className="container-kesult">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Benefits */}
            <div>
              <h2 className="heading-2 mb-8">
                {language === 'nl' ? 'WAT TE VERWACHTEN' : 'WHAT TO EXPECT'}
              </h2>
              
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-6 border-2 border-[#F1F0EF] rounded-[2rem] hover:border-[#4286F4] transition-colors"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-[#4286F4] rounded-xl flex items-center justify-center text-white">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1">{benefit.title}</h3>
                      <p className="text-[#71717A]">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-[#F1F0EF] border-l-4 border-[#4286F4]">
                <h4 className="font-bold mb-2">
                  {language === 'nl' ? 'Na de Intake:' : 'After the Intake:'}
                </h4>
                <ul className="space-y-2 text-sm text-[#71717A]">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#4286F4]" />
                    {language === 'nl' ? 'Quick Scan van je digitale aanwezigheid' : 'Quick Scan of your digital presence'}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#4286F4]" />
                    {language === 'nl' ? 'Voorstel op maat met aanbevolen pakket' : 'Tailored proposal with recommended package'}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#4286F4]" />
                    {language === 'nl' ? 'Geen verplichtingen' : 'No obligations'}
                  </li>
                </ul>
              </div>

              {/* Testimonial/Trust */}
              <div className="mt-8 p-6 bg-[#0A0A0A] text-white rounded-2xl">
                <p className="text-lg italic mb-4">
                  {language === 'nl'
                    ? '"Falco hielp ons de organische traffic van 30.000 naar 100.000+ maandelijkse bezoekers te brengen."'
                    : '"Falco helped us grow organic traffic from 30,000 to 100,000+ monthly visitors."'}
                </p>
                <p className="text-sm text-[#A1A1AA]">— Alpina.nl</p>
              </div>
            </div>

            {/* Form */}
            <div className="bg-[#F1F0EF] p-8 md:p-12 border-2 border-[#F1F0EF] rounded-[2rem] shadow-2xl">
              <h2 className="heading-3 mb-6">
                {language === 'nl' ? 'Plan je Intake Call' : 'Book your Intake Call'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="intake-form">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('quickScan.form.name')} *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-kesult"
                    data-testid="intake-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('quickScan.form.email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-kesult"
                    data-testid="intake-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">{t('quickScan.form.company')}</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="input-kesult"
                    data-testid="intake-company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">{t('quickScan.form.website')} *</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.website}
                    onChange={handleChange}
                    required
                    className="input-kesult"
                    data-testid="intake-website-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('quickScan.form.phone')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-kesult"
                    data-testid="intake-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t('quickScan.form.message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="input-kesult resize-none"
                    placeholder={language === 'nl' ? 'Bijv. Je belangrijkste SEO doelen, huidige uitdagingen...' : 'E.g. Your main SEO goals, current challenges...'}
                    data-testid="intake-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="intake-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {language === 'nl' ? 'Verzenden...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      {t('quickScan.form.submit')}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-[#71717A] text-center">
                  {language === 'nl' 
                    ? 'Je gegevens worden alleen gebruikt om contact met je op te nemen over de Intake Call.'
                    : 'Your data will only be used to contact you about the Intake Call.'}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
